// src/Components/Admin/SubtelRoleSwitcher.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import GeneralContext from '../GeneralContext/GeneralContext';
import { Box } from '@mui/system';
import {
  Autocomplete, TextField, Stack, Typography,
  Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PageButtonBlue from '../GeneralComponents/PageButtonBlue';
import axios from '../../api/axios';

import {
  ROLE_LABELS,
  ROL_ADMIN_SUBTEL,
  ROL_VAL_SUBTEL,
} from '../../Constants/Constants';

// Roles SUBTEL habilitados para usar este panel
const SUBTEL_ROLE_IDS = [ROL_ADMIN_SUBTEL, ROL_VAL_SUBTEL];

const SubtelRoleSwitcher = () => {
  const {
    serverUser,
    userSesion,
    effectiveRoleId,
    updateUserIdentity,
    resetUserIdentityOverrides,
    reloadChildren,
  } = useContext(GeneralContext);

  const [operadores, setOperadores] = useState([]);
  const [expanded, setExpanded] = useState(false); // colapsado por defecto

  // Identidad original (backend) vs. identidad efectiva (posible suplantación).
  // Se deriva del contexto para que sobreviva a reloadChildren() y no dependa
  // de estado local (que se reinicia al remontar).
  const originalId = serverUser?.rut ?? serverUser?.id ?? null;
  const currentId = userSesion?.rut ?? userSesion?.id ?? null;
  const overrideActive =
    !!originalId && !!currentId && String(originalId) !== String(currentId);

  // Detección basada en el rol ORIGINAL (backend), para que el panel
  // siga visible aunque la identidad efectiva se cambie a OPERADOR.
  const isSubtel = useMemo(
    () => (serverUser?.roles ?? []).some(r => SUBTEL_ROLE_IDS.includes(r.role_id)),
    [serverUser?.roles]
  );

  const originalRoleName = useMemo(() => {
    const original = (serverUser?.roles ?? []).find(r => SUBTEL_ROLE_IDS.includes(r.role_id));
    return original?.nombre_rol ?? ROLE_LABELS[original?.role_id] ?? 'SUBTEL';
  }, [serverUser?.roles]);

  const currentRoleName = effectiveRoleId
    ? (ROLE_LABELS[effectiveRoleId] ?? `Rol #${effectiveRoleId}`)
    : (userSesion?.roles?.[0]?.nombre_rol ?? `${originalRoleName} (original)`);

  const activeOperador = useMemo(
    () => operadores.find((o) => String(o.rut) === String(currentId)) ?? null,
    [operadores, currentId]
  );
  const activeOperadorLabel = activeOperador
    ? `${activeOperador.nombre}${activeOperador.operador ? ` (${activeOperador.operador})` : ''}`
    : (userSesion?.nombre ?? currentId ?? '');

  // Prepoblar la lista de operadores desde el backend (solo perfiles SUBTEL).
  useEffect(() => {
    if (!isSubtel) return;
    let active = true;
    (async () => {
      try {
        const resp = await axios.getOperadoresUsuarios();
        if (active) setOperadores(resp?.data ?? []);
      } catch (e) {
        console.error('Error cargando operadores:', e);
      }
    })();
    return () => { active = false; };
  }, [isSubtel]);

  if (!userSesion || !isSubtel) return null;

  const applyUser = async (rut) => {
    if (!rut) return;
    try {
      const userInfo = await axios.getAdminUserData(rut);
      updateUserIdentity(userInfo?.data);
      reloadChildren?.();
    } catch (e) {
      console.error('Error cargando info usuario:', e);
    }
  };

  const handleChange = (rut) => {
    applyUser(rut);
    setExpanded(false);
  };

  const handleRestore = () => {
    resetUserIdentityOverrides();
    reloadChildren?.();
    setExpanded(false);
  };

  // ===== Estado suplantación activa: se oculta el switch y se muestra
  // únicamente un banner de advertencia prominente. =====
  if (overrideActive) {
    return (
      <Box
        sx={{
          bgcolor: '#FEF3C7',
          border: '1px solid #F59E0B',
          borderRadius: 2,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
          <VisibilityIcon sx={{ fontSize: 48, color: '#B45309' }} />
          <Box sx={{ pl: 1 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 0.5, color: '#92400E' }}>
              Estás navegando como otro usuario
            </Typography>
            <Typography variant="body2" sx={{ color: '#92400E' }}>
              Estás viendo la plataforma como <strong>{activeOperadorLabel}</strong>. Tu cuenta
              SUBTEL no cambia; puedes volver a tu vista cuando quieras.
            </Typography>
          </Box>
        </Stack>

        <PageButtonBlue onClick={handleRestore} width="12rem">
          Volver a mi rol
        </PageButtonBlue>
      </Box>
    );
  }

  // ===== Estado normal: switch para elegir un operador. =====
  const cardStyle = { border: '1px solid #e5e7eb', borderRadius: 2, background: '#fafafa' };
  const badgeStyle = {
    fontSize: 12, padding: '2px 8px', borderRadius: 4,
    background: '#DBEAFE', color: '#1E3A8A', border: '1px solid #93C5FD'
  };

  return (
    <Accordion
      disableGutters
      expanded={expanded}
      onChange={(e, isExp) => setExpanded(isExp)}
      sx={cardStyle}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Typography sx={{ fontWeight: 700 }}>
            Switch de roles (SUBTEL)
          </Typography>
          <span style={badgeStyle}>SUBTEL (original)</span>
          <Typography variant="body2" sx={{ ml: 1, color: '#6b7280' }}>
            Rol actual: <strong>{currentRoleName}</strong>
          </Typography>
          {!!(userSesion?.rut || userSesion?.id) && (
            <Typography variant="body2" sx={{ ml: 1, color: '#6b7280' }}>
              RUT/ID: <strong>{userSesion?.rut ?? userSesion?.id}</strong>
            </Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            Cambia temporalmente tu visualización a la de un usuario operador.
          </Typography>

          <Autocomplete
            size="small"
            options={operadores}
            value={activeOperador}
            onChange={(e, newValue) => {
              if (newValue?.rut) handleChange(newValue.rut);
              else handleRestore();
            }}
            isOptionEqualToValue={(o, v) => o.rut === v.rut}
            getOptionLabel={(o) =>
              `${o.nombre} — ${o.rol}${o.operador ? ` (${o.operador})` : ''} — ${o.rut}`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Operador"
                placeholder={`${originalRoleName} (original)`}
              />
            )}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default SubtelRoleSwitcher;
